import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toaster, toast } from 'react-hot-toast'
import { detectPlatformAdapter, MockPlatformAdapter } from './platform'
import { api, ApiError, configureApi, setApiJwt } from './api/client'
import { useSession } from './store/user.store'
import { usePaywallStore } from './store/paywall.store'
import { LoadingDots } from './components/ui/LoadingDots'
import { ErrorState } from './components/ui/ErrorState'

type Status = 'loading' | 'ready' | 'error' | 'no-platform' | 'sdk-blocked'

interface SdkBlockedInfo {
  platform: 'max'
}

export function App() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [sdkBlocked, setSdkBlocked] = useState<SdkBlockedInfo | null>(null)
  const setSession = useSession((s) => s.setSession)
  const showPaywall = usePaywallStore((s) => s.show)

  useEffect(() => {
    configureApi({
      on401: () => {
        toast.error('Сессия истекла, перезагружаем')
        setTimeout(() => window.location.reload(), 1000)
      },
      on402: (err) => {
        const detail = err.details
        showPaywall({
          reason: (detail?.reason as 'feature_locked' | 'free_quota_exhausted') ?? 'feature_locked',
          feature: detail?.feature as string | undefined,
          message: err.body.error.message,
        })
      },
    })
  }, [showPaywall])

  useEffect(() => {
    const adapter = detectPlatformAdapter()
    // In production the only legitimate paths are TG WebApp or MAX bridge.
    // If we landed in MockAdapter (e.g. user opened the Railway URL directly
    // in a browser tab), the API will reject `yadviga_dev_mock_v1` with
    // missing_hash. Surface a clear instruction instead of a cryptic error.
    const isMockInProd = import.meta.env.PROD && adapter instanceof MockPlatformAdapter
    if (isMockInProd) {
      // TG no longer needs a "blocked" branch — we bundle @tma.js/sdk,
      // so a missing telegram.org doesn't matter. The native bridge is
      // injected by the TG client itself. Only MAX still depends on an
      // external script (st.max.ru).
      const sdkLoad = window.__yadvigaSdkLoad
      if (sdkLoad?.max === 'failed') {
        setSdkBlocked({ platform: 'max' })
        setStatus('sdk-blocked')
        return
      }
      setStatus('no-platform')
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const init = await adapter.init()
        const res = await api.authInit({
          platform: adapter.platform === 'max' ? 'max' : 'telegram',
          initData: init.initDataRaw,
          languageHint: adapter.getLanguageHint(),
        })
        if (cancelled) return
        setApiJwt(res.jwt)
        setSession({ user: res.user, jwt: res.jwt, referralLink: res.referralLink })
        await i18n.changeLanguage(res.user.language)
        setStatus('ready')

        // Handle deep-link redeem. Legacy `compat_<id>` is the pre-deeplink
        // form; new shared links carry `ci_<token>` from buildCompatInviteLink.
        // `tr_<token>` opens a shared tarot reading.
        const startParam = adapter.getStartParam()
        if (startParam && startParam.startsWith('compat_')) {
          navigate(`/compat/redeem?token=${startParam}`, { replace: true })
        } else if (startParam && startParam.startsWith('ci_')) {
          navigate(`/compat/redeem?token=${startParam.slice(3)}`, { replace: true })
        } else if (startParam && startParam.startsWith('tr_')) {
          navigate(`/tarot/shared/${startParam.slice(3)}`, { replace: true })
        } else if (res.isNew) {
          navigate('/onboarding', { replace: true })
        }
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof ApiError ? e.body.error.message : (e as Error).message
        setErrorMsg(msg)
        setStatus('error')
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === 'loading') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/icons/logo.png"
            alt=""
            aria-hidden="true"
            className="h-14 w-14 select-none"
            draggable={false}
          />
          <p className="text-sage-700">{t('app.loading')}</p>
          <LoadingDots />
        </div>
      </main>
    )
  }

  if (status === 'error') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6">
        <ErrorState message={errorMsg ?? undefined} onRetry={() => window.location.reload()} />
      </main>
    )
  }

  if (status === 'no-platform') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="flex max-w-md flex-col items-center gap-4">
          <img
            src="/icons/logo.png"
            alt=""
            aria-hidden="true"
            className="h-14 w-14 select-none"
            draggable={false}
          />
          <h1 className="text-xl font-semibold text-sage-800">Открой через бота</h1>
          <p className="text-sage-700">
            Ядвига живёт внутри Telegram и MAX. Открой меню в чате с{' '}
            <a
              className="underline"
              href="https://t.me/yadviga_app_bot"
              target="_blank"
              rel="noopener noreferrer"
            >
              @yadviga_app_bot
            </a>{' '}
            в Telegram или найди «Ядвигу» в MAX — и я тебя встречу там.
          </p>
        </div>
      </main>
    )
  }

  if (status === 'sdk-blocked' && sdkBlocked) {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="flex max-w-md flex-col items-center gap-4">
          <img
            src="/icons/logo.png"
            alt=""
            aria-hidden="true"
            className="h-14 w-14 select-none"
            draggable={false}
          />
          <h1 className="text-xl font-semibold text-sage-800">Не загрузилась библиотека MAX</h1>
          <p className="text-sage-700">
            Не удалось подключиться к{' '}
            <code className="rounded bg-sage-100 px-1 text-sm">st.max.ru</code> — возможно,
            Cloudflare-прокси перепутал скрипты или сеть нестабильна. Попробуй открыть мини-апп
            заново через минуту.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-2xl bg-sage-600 px-5 py-2.5 text-cream transition hover:bg-sage-700"
          >
            Попробовать ещё раз
          </button>
        </div>
      </main>
    )
  }

  return (
    <>
      <Outlet />
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#faf6ee', color: '#34442b', border: '1px solid #cdd9be' },
        }}
      />
    </>
  )
}
